import { Box, Typography, Card, Stack, Button, Chip, CircularProgress, Alert, Input, IconButton, Tooltip, Table, Sheet, Avatar, List, ListItem, ListItemContent, useTheme } from "@mui/joy";
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../context";
import { APIGetAssignedTests, APIDeleteAssignedTest } from "../../api";
import { useNavigate } from "react-router";
import { useMediaQuery } from "@mui/material";
import { SearchRounded, AddRounded, EditRounded, DeleteRounded, VisibilityRounded, CheckCircleRounded, CancelRounded, FilterListRounded, PersonRounded, AssignmentRounded, CalendarTodayRounded, AccessTimeRounded, PlayArrowRounded } from '@mui/icons-material';



export default function TestAssignmentsPage() {
    const { token, currentDoctor, setModalAssignTest, modalAssignTest } = useContext(LayoutContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    
    // Фильтры
    const [filters, setFilters] = useState({
        status: '',
        patient_id: '',
        doctor_id: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    
    // Проверяем права пользователя
    const isAdmin = (currentDoctor?.type_profile.includes('admin') || currentDoctor?.type_profile?.includes('admin')) || (currentDoctor?.type_profile.includes('supervisor') || currentDoctor?.type_profile?.includes('supervisor'));
    
    // Загрузка назначенных тестов
    const loadAssignments = async (params = {}) => {
        try {
            setLoading(true);
            setError(null);
            
            // Если пользователь не админ, фильтруем по доктору
            if (!isAdmin && currentDoctor?.id) {
                params.doctor_id = currentDoctor.id;
            }
            
            // Добавляем поисковый запрос
            if (searchTerm) {
                params.search = searchTerm;
            }
            
            // Добавляем фильтры
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    params[key] = filters[key];
                }
            });
            
            const response = await APIGetAssignedTests(token, params);
            
            if (response.success) {
                setAssignments(response.sessions || []);
            } else {
                setError(response.error || 'Ошибка при загрузке назначений');
            }
        } catch (error) {
            console.error("Ошибка при загрузке назначений:", error);
            setError("Ошибка при загрузке назначений");
        } finally {
            setLoading(false);
        }
    };
    
    // Удаление назначения
    const handleDeleteAssignment = async (sessionId, patientName, testName) => {
        if (!window.confirm(`Вы уверены, что хотите отменить назначение теста "${testName}" для пациента "${patientName}"?`)) {
            return;
        }
        
        try {
            setDeletingId(sessionId);
            const response = await APIDeleteAssignedTest(token, sessionId);
            
            if (response.success) {
                // Обновляем список
                await loadAssignments();
                setError(null);
            } else {
                setError(response.error || 'Ошибка при отмене назначения');
            }
        } catch (error) {
            console.error("Ошибка при отмене назначения:", error);
            setError("Ошибка при отмене назначения");
        } finally {
            setDeletingId(null);
        }
    };
    
    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return 'Не указано';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };
    
    // Форматирование даты с временем
    const formatDateTime = (dateString) => {
        if (!dateString) return 'Не указано';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    // Получение цвета статуса
    const getStatusColor = (status) => {
        switch (status) {
            case 'appointed': return 'primary';
            case 'started': return 'warning';
            case 'in_progress': return 'info';
            case 'completed': return 'success';
            case 'abandoned': return 'danger';
            case 'paused': return 'neutral';
            default: return 'neutral';
        }
    };
    
    // Получение текста статуса
    const getStatusText = (status) => {
        switch (status) {
            case 'appointed': return 'Назначен';
            case 'started': return 'Начат';
            case 'in_progress': return 'В процессе';
            case 'completed': return 'Завершен';
            case 'abandoned': return 'Прерван';
            case 'paused': return 'Приостановлен';
            default: return status;
        }
    };
    
    // Инициализация
    useEffect(() => {
        loadAssignments();
    }, []);

    // Если меняется модальная форма
    useEffect(() => {
        if (!modalAssignTest)
            loadAssignments();
    }, [modalAssignTest]);
    
    // Десктопная версия таблицы
    const DesktopTable = () => (
        <Sheet sx={{ height: 'auto', overflow: 'auto' }}>
            <Table
                aria-label="Таблица назначенных тестов"
                stripe="odd"
                hoverRow
                sx={{
                    '& thead th': { fontWeight: 'lg' },
                    '& tbody tr:hover': { backgroundColor: 'background.level1' },
                }}
            >
                <thead>
                    <tr>
                        <th>Пациент</th>
                        <th>Тест</th>
                        <th>Статус</th>
                        <th>Врач</th>
                        <th>Дата назначения</th>
                        <th style={{ textAlign: 'center' }}>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {assignments.map((assignment) => (
                        <tr key={assignment.id}>
                            <td>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Avatar size="sm">
                                        <PersonRounded />
                                    </Avatar>
                                    <Box>
                                        <Typography level="body-sm" fontWeight="lg">
                                            {assignment.user_info?.name || 'Не указано'}
                                        </Typography>
                                        {assignment.user_info?.phone && (
                                            <Typography level="body-xs" color="neutral">
                                                {assignment.user_info.phone}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </td>
                            <td>
                                <Typography level="body-sm">
                                    {assignment.test_info?.name || 'Тест'}
                                </Typography>
                                {assignment.test_info?.test_type && (
                                    <Typography level="body-xs" color="neutral">
                                        {assignment.test_info.test_type === 'psychology' ? 'Психологический' :
                                         assignment.test_info.test_type === 'diagnostic' ? 'Диагностический' :
                                         assignment.test_info.test_type === 'screening' ? 'Скрининговый' : 'Самооценка'}
                                    </Typography>
                                )}
                            </td>
                            <td>
                                <Chip
                                    size="sm"
                                    variant="soft"
                                    color={getStatusColor(assignment.status)}
                                    startDecorator={
                                        assignment.status === 'completed' ? <CheckCircleRounded /> :
                                        assignment.status === 'abandoned' ? <CancelRounded /> :
                                        <AccessTimeRounded />
                                    }
                                >
                                    {getStatusText(assignment.status)}
                                </Chip>
                            </td>
                            <td>
                                {assignment.doctor_info ? (
                                    <Typography level="body-sm">
                                        {assignment.doctor_info.name}
                                    </Typography>
                                ) : (
                                    <Typography level="body-sm" color="neutral">
                                        Не назначен
                                    </Typography>
                                )}
                            </td>
                            <td>
                                <Typography level="body-sm">
                                    {formatDateTime(assignment.date_create)}
                                </Typography>
                                {assignment.started_at && (
                                    <Typography level="body-xs" color="neutral">
                                        Начат: {formatDate(assignment.started_at)}
                                    </Typography>
                                )}
                            </td>
                            <td>
                                <Stack direction="row" spacing={1} justifyContent="center">
                                    {(isAdmin || assignment.doctor_info?.id === currentDoctor?.id) && 
                                     assignment.status === 'appointed' && (
                                        <Tooltip title="Отменить назначение">
                                            <IconButton
                                                size="sm"
                                                variant="outlined"
                                                color="danger"
                                                loading={deletingId === assignment.id}
                                                onClick={() => handleDeleteAssignment(
                                                    assignment.id,
                                                    assignment.user_info?.name,
                                                    assignment.test_info?.name
                                                )}
                                            >
                                                <DeleteRounded />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Sheet>
    );
    
    // Мобильная версия списка
    const MobileList = () => (
        <List
            sx={{
                '--ListItem-paddingY': '1rem',
                '--ListItem-paddingX': '1rem',
                '--ListItem-borderRadius': '8px',
                '--ListItem-gap': '1rem',
            }}
        >
            {assignments.map((assignment) => (
                <Card
                    key={assignment.id}
                    variant="outlined"
                    sx={{
                        mb: 2,
                        p: 0,
                        '&:hover': {
                            boxShadow: 'md',
                            borderColor: 'neutral.outlinedHoverBorder',
                        },
                    }}
                >
                    <ListItem>
                        <ListItemContent>
                            <Stack spacing={2}>
                                {/* Пациент */}
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Avatar size="sm">
                                        <PersonRounded />
                                    </Avatar>
                                    <Box>
                                        <Typography level="title-md">
                                            {assignment.user_info?.name || 'Не указано'}
                                        </Typography>
                                        {assignment.user_info?.phone && (
                                            <Typography level="body-xs" color="neutral">
                                                {assignment.user_info.phone}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                                
                                {/* Тест */}
                                <Box>
                                    <Typography level="title-sm">
                                        {assignment.test_info?.name || 'Тест'}
                                    </Typography>
                                    {assignment.test_info?.test_type && (
                                        <Typography level="body-xs" color="neutral">
                                            {assignment.test_info.test_type === 'psychology' ? 'Психологический' :
                                             assignment.test_info.test_type === 'diagnostic' ? 'Диагностический' :
                                             assignment.test_info.test_type === 'screening' ? 'Скрининговый' : 'Самооценка'}
                                        </Typography>
                                    )}
                                </Box>
                                
                                {/* Статус и врач */}
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Chip
                                        size="sm"
                                        variant="soft"
                                        color={getStatusColor(assignment.status)}
                                        startDecorator={
                                            assignment.status === 'completed' ? <CheckCircleRounded /> :
                                            assignment.status === 'abandoned' ? <CancelRounded /> :
                                            <AccessTimeRounded />
                                        }
                                    >
                                        {getStatusText(assignment.status)}
                                    </Chip>
                                    
                                    {assignment.doctor_info && (
                                        <Typography level="body-xs" color="neutral">
                                            Врач: {assignment.doctor_info.name}
                                        </Typography>
                                    )}
                                </Stack>
                                
                                {/* Дата */}
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <CalendarTodayRounded sx={{ fontSize: '0.875rem', color: 'neutral.500' }} />
                                    <Typography level="body-xs" color="neutral">
                                        Назначен: {formatDate(assignment.date_create)}
                                    </Typography>
                                </Stack>
                                
                                {/* Действия */}
                                <Stack direction="row" spacing={1} justifyContent="space-between">
                                    {(isAdmin || assignment.doctor_info?.id === currentDoctor?.id) && 
                                     assignment.status === 'appointed' && (
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            color="danger"
                                            startDecorator={<DeleteRounded />}
                                            loading={deletingId === assignment.id}
                                            onClick={() => handleDeleteAssignment(
                                                assignment.id,
                                                assignment.user_info?.name,
                                                assignment.test_info?.name
                                            )}
                                            fullWidth
                                        >
                                            Отменить
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                        </ListItemContent>
                    </ListItem>
                </Card>
            ))}
        </List>
    );
    
    return (
        <>
            <Box sx={{ display: 'flex' }}>
                <Box
                    component="main"
                    className="MainContent"
                    sx={{
                        px: { xs: 2, md: 6 },
                        pt: 2,
                        pb: { xs: 2, sm: 2, md: 3 },
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        gap: 2,
                    }}
                >
                    {/* Заголовок и кнопки */}
                    <Box
                        sx={{
                            display: 'flex',
                            mb: 1,
                            gap: 1,
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'start', sm: 'center' },
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography level="h2" component="h1" startDecorator={<AssignmentRounded />}>
                            Назначенные тесты
                        </Typography>
                        
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant={showFilters ? "solid" : "outlined"}
                                color="neutral"
                                startDecorator={<FilterListRounded />}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                Фильтры
                            </Button>
                            <Button
                                variant="solid"
                                color="primary"
                                startDecorator={<AddRounded />}
                                onClick={() => setModalAssignTest(true)}
                            >
                                Назначить тест
                            </Button>
                        </Stack>
                    </Box>
                    
                    {/* Сообщения об ошибках */}
                    {error && (
                        <Alert color="danger" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    {/* Поиск */}
                    <Input
                        size="sm"
                        placeholder="Поиск по пациентам или тестам..."
                        startDecorator={<SearchRounded />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && loadAssignments()}
                        endDecorator={
                            <Button size="sm" onClick={() => loadAssignments()}>
                                Найти
                            </Button>
                        }
                    />
                    
                    {/* Фильтры */}
                    {showFilters && (
                        <Card variant="outlined" sx={{ p: 2 }}>
                            <Typography level="title-sm" sx={{ mb: 2 }}>
                                Фильтры
                            </Typography>
                            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
                                <Select
                                    size="sm"
                                    placeholder="Статус"
                                    value={filters.status}
                                    onChange={(e, value) => setFilters(prev => ({ ...prev, status: value }))}
                                    sx={{ flex: 1 }}
                                >
                                    <Option value="">Все статусы</Option>
                                    <Option value="appointed">Назначен</Option>
                                    <Option value="started">Начат</Option>
                                    <Option value="in_progress">В процессе</Option>
                                    <Option value="completed">Завершен</Option>
                                    <Option value="abandoned">Прерван</Option>
                                    <Option value="paused">Приостановлен</Option>
                                </Select>
                                
                                <Button variant="outlined" color="neutral" onClick={() => {
                                    setFilters({ status: '', patient_id: '', doctor_id: '' });
                                    loadAssignments();
                                }}>
                                    Сбросить
                                </Button>
                                <Button onClick={() => loadAssignments()}>
                                    Применить
                                </Button>
                            </Stack>
                        </Card>
                    )}
                    
                    {/* Список назначений */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : assignments.length > 0 ? (
                        <>
                            {/* Показываем количество на мобильных */}
                            {isMobile && (
                                <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                                    Найдено назначений: {assignments.length}
                                </Typography>
                            )}
                            
                            {isMobile ? <MobileList /> : <DesktopTable />}
                        </>
                    ) : (
                        <Alert color="neutral" sx={{ mt: 2 }}>
                            Нет назначенных тестов
                        </Alert>
                    )}
                </Box>
            </Box>
        </>
    );
}